import {
  DevicesService,
  Dimmer_DimmerEffectSchedulerEffectsModel,
  Dimmer_RunDimmerEffect,
  Dimmer_SchedulerStateModel,
  Dimmer_SetDimmerChannelsStateModel,
  Dimmer_SetDimmerEffectsDelayModel,
  Dimmer_SetDimmerFadeModel,
  Dimmer_SetDimmerRelayModel,
  DimmerDetails,
  DimmerService,
  OnOffTimeModel,
  StateModel,
  UpdatePrivateDescriptionModel,
} from "../api";
import {DimmerParams} from "../models/Dimmer.model";
import {
  mapDimmerParamsResponse,
  mapDimmerSchedulerModeToContract,
  mapDimmerSchedulerTimeResponse,
  mapSensorBlockGpsCoordinatesResponse,
} from "../shared/Helpers.utils";

export const DimmerApi = {

  // Получение параметров диммера
  getDimmerParams: async (id: string): Promise<DimmerParams> => {
    const response = await DimmerService.getApiDimmerParams(id);
    return mapDimmerParamsResponse(response as Record<string, unknown>);
  },

  // Получение описания диммера
  getDimmerDescription: async (id: string): Promise<any> => {
    const response = await DimmerService.getApiDimmer(id);

    return {
      details: {
        channels: response.details?.channels ?? [],
        isCircularEffectsMode: response.details?.isCircularEffectsMode ?? false,
      },
    };
  },

  // Получение времени
  getTimeScheduler: async (id: string) => {
    const response = await DimmerService.getApiDimmerSchedulerTime(id);
    return mapDimmerSchedulerTimeResponse(response as Record<string, unknown>);
  },

  // Получение восход / закат
  getDuskDawnScheduler: async (id: string) => {
    const response = await DimmerService.getApiDimmerSchedulerTimeDuskDawn(id);
    return mapDimmerSchedulerTimeResponse(response as Record<string, unknown>);
  },

  // Получение эффектов
  getEffects: async (id: string) => {
    return DimmerService.getApiDimmerSchedulerEffects(id);
  },

  // Обновление эффектов
  updateEffects: async (
    id: string,
    effects: Dimmer_DimmerEffectSchedulerEffectsModel
  ): Promise<boolean> => {
    await DimmerService.postApiDimmerSchedulerEffects(id, effects);
    return true;
  },

  // Запуск теста 4 каналов
  runTestFourChannelEffects: async (id: string, effect: string): Promise<boolean> => {
    const payload: Dimmer_RunDimmerEffect = {effect: effect as Dimmer_RunDimmerEffect["effect"]};
    await DimmerService.postApiDimmerEffectsRunClock(id, payload);
    return true;
  },

  // Запуск теста каждого канала
  runTestAllChannelEffects: async (id: string, effect: string): Promise<boolean> => {
    const payload: Dimmer_RunDimmerEffect = {effect: effect as Dimmer_RunDimmerEffect["effect"]};
    await DimmerService.postApiDimmerEffectsRunAll(id, payload);
    return true;
  },

  // х3
  updateCircularEffects: async (id: string, enabled: boolean): Promise<boolean> => {
    const payload: StateModel = {enabled};
    await DimmerService.postApiDimmerCircularEffects(id, payload);
    return true;
  },

  // Получение gps координат
  getGpsCoordinates: async (id: string) => {
    const response = await DimmerService.getApiDimmerGpsCoordinates(id);
    return mapSensorBlockGpsCoordinatesResponse(response as Record<string, unknown>);
  },

  // Обновление стейта каналов
  updateChannelsState: async (
    id: string,
    payload: Record<string, any>
  ): Promise<boolean> => {
    const requestBody: Dimmer_SetDimmerChannelsStateModel = {
      channels: Array.isArray(payload.channels)
        ? payload.channels.map((channel: Record<string, unknown>) => ({
            isEnabled: Boolean(channel.isEnabled),
            brightness: Number(channel.brightness ?? 0),
          }))
        : [],
    };

    await DimmerService.postApiDimmerChannelsState(id, requestBody);
    return true;
  },

  // Обновление описания каналов
  updateChannelDescription: async (id: string, payload: Record<string, any>): Promise<boolean> => {
    const requestBody: DimmerDetails = {
      channels: Array.isArray(payload.channels)
        ? payload.channels.map((channel: Record<string, unknown>) => ({
            channel: Number(channel.channel ?? 0),
            description: String(channel.description ?? ""),
          }))
        : [],
    };

    await DimmerService.putApiDimmer(id, requestBody);
    return true;
  },

  // Обновление описания (админ)
  updatePrivateDescription: async (id: string, privateDescription: string): Promise<boolean> => {
    const payload: UpdatePrivateDescriptionModel = {privateDescription};
    await DevicesService.putApiDevicesDescriptionPrivate(id, payload);
    return true;
  },

  // Обновление стейта реле
  updateRelayState: async (id: string, enabled: boolean): Promise<boolean> => {
    const payload: StateModel = {enabled};
    await DimmerService.postApiDimmerRelayState(id, payload);
    return true;
  },

  // Обновление режима реле
  updateRelayMode: async (id: string, mode: number): Promise<boolean> => {
    const payload: Dimmer_SetDimmerRelayModel = {
      mode: mapDimmerSchedulerModeToContract(mode),
    };

    await DimmerService.postApiDimmerRelayMode(id, payload);
    return true;
  },

  // Обновление задержки эффектов
  updateEffectDelay: async (id: string, delay: string | number): Promise<boolean> => {
    const payload: Dimmer_SetDimmerEffectsDelayModel = {
      delay: Number(delay),
    };

    await DimmerService.postApiDimmerEffectsDelay(id, payload);
    return true;
  },

  // Обновление плавности эффектов
  updateEffectFade: async (id: string, fade: string | number): Promise<boolean> => {
    const payload: Dimmer_SetDimmerFadeModel = {
      fade: Number(fade),
    };

    await DimmerService.postApiDimmerChannelsFade(id, payload);
    return true;
  },

  // Обновление режима
  updateModeChange: async (id: string, mode: number | string): Promise<boolean> => {
    const payload: Dimmer_SchedulerStateModel = {
      mode: mapDimmerSchedulerModeToContract(mode),
    };

    await DimmerService.postApiDimmerSchedulerMode(id, payload);
    return true;
  },

  // Обновление стейта света
  updateLightState: async (id: string, enabled: boolean): Promise<boolean> => {
    const payload: StateModel = {enabled};
    await DimmerService.postApiDimmerLightState(id, payload);
    return true;
  },

  // Обновление стейта эффектов в любое время
  updateEffectsAlwaysEnabledState: async (id: string, enabled: boolean): Promise<boolean> => {
    const payload: StateModel = {enabled};
    await DimmerService.postApiDimmerEffectsRunAlways(id, payload);
    return true;
  },

  // Обновление времени реле
  updateTimeScheduler: async (id: string, payload: Record<string, string>): Promise<boolean> => {
    const requestBody: OnOffTimeModel = {
      onTime: String(payload.onTime ?? "00:00:00"),
      offTime: String(payload.offTime ?? "00:00:00"),
    };

    await DimmerService.postApiDimmerSchedulerTime(id, requestBody);
    return true;
  },
};

export default DimmerApi;
